import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getFormValues, destroy } from 'redux-form';
import { createSelector } from 'reselect';
import * as actions from './login.actions';
import {
    selectors as bookmakersInfoSelectors,
    actions as bookmakersInfoActions,
} from '../bookmakersInfo';
import {
    actions as bookmakersListActions,
    selectors as bookmakersListSelectors,
    constants as bookmakersListConstants,
} from '../bookmakersList';
import { actions as freeBetsActions } from '../freeBets';
import { actions as gdprActions } from '../gdpr';
import {
    selectors as routerSelectors,
} from '../transitionRouter';
import Login from './components/Login';
import { create as createLogger } from '../utils/logger';
import { setLoginCookie } from '../utils/loginCookies';
import getBookmakersConfig from '../bookmakersList/bookmakers';

const logger = createLogger('Login');
const bookmakers = getBookmakersConfig();

export class LoginContainer extends Component {

    constructor(props) {
        super(props);
        this.state = {
            shouldNavigateBack: false,
        };
    }

    componentWillMount() {
        const { apiName, feed, loginType, setLogIn } = this.props;
        const isIframeLogin =
            loginType === bookmakersListConstants.LOGIN_TYPE.IFRAME;
        const isWindowLogin =
            loginType === bookmakersListConstants.LOGIN_TYPE.WINDOW;
        if (isIframeLogin || isWindowLogin) {
            this.props.loadBookmakerLoginInfo(apiName, feed);
        }
        setLogIn(feed);
    }

    componentWillUnmount() {
        this.props.clearError();
        this.props.destroyLoginForm();
    }

    onLoginSubmit = ({ saveUsername, username }) => {
        if (saveUsername && username) {
            this.props.saveBookmakerUsername(this.props.feed, username);
        }
        return this.login();
    }

    onLoginSuccess = (data) => {
        const {
            apiName,
            feed,
            getUserInfo,
            clearFreeBets,
            onLoginSuccess,
            setLogInSuccess,
            showGdprPopup,
        } = this.props;
        const logoutBookmaker = bookmakers.find(bookmaker => bookmaker.feed === feed);

        clearFreeBets()
        const promises = [];
        promises.push(getUserInfo(apiName, feed))

        if (data && data.termsAndConditions) {
            showGdprPopup(data.termsAndConditions)
        }

        return Promise.all(promises)
            .catch(err => logger.error(err))
            .then(() => {
                setLoginCookie(
                    logoutBookmaker.cookieName,
                    logoutBookmaker.loginDuration,
                );
                onLoginSuccess();
                setLogInSuccess(feed);
            })
            .then(() => {
                const loggedInEvent = new CustomEvent('BETSLIP.BOOKMAKER.LOGGED_IN', {
                    detail: {},
                });
                document.dispatchEvent(loggedInEvent);
            })
    }

    onNavigateBack = () => {
        this.setState({ shouldNavigateBack: true });
    }

    onSaveUsernameChange = (event, checked) => {
        if (!checked) {
            this.props.removeBookmakerUsername(this.props.feed);
        }
    }

    cancelError = () => {
        const { loginType } = this.props;
        const isIframeLogin = loginType === bookmakersListConstants.LOGIN_TYPE.IFRAME;
        const isWindowLogin = loginType === bookmakersListConstants.LOGIN_TYPE.WINDOW;
        if (isIframeLogin || isWindowLogin) {
            this.setState({ shouldNavigateBack: true });
        } else {
            this.props.clearError();
        }
    }

    retry = () => {
        const { apiName, feed, loginType } = this.props;
        const isIframeLogin = loginType === bookmakersListConstants.LOGIN_TYPE.IFRAME;
        const isWindowLogin = loginType === bookmakersListConstants.LOGIN_TYPE.WINDOW;
        if (isIframeLogin || isWindowLogin) {
            this.props.loadBookmakerLoginInfo(apiName, feed);
        } else {
            this.retryLogin()
                .then(this.onLoginSuccess);
        }
    }

    login = () => {
        const { apiName, feed, clearError, formValues, setLogInFailure } = this.props;
        if (!!formValues.username && !!formValues.password) {
            clearError();
            return this.props
                .onLogin(apiName, feed, formValues)
                .then(this.onLoginSuccess)
                .catch(({ retry, response }) => {
                    let errMessage = '';
                    if (
                        response &&
                        response.data &&
                        response.data.errors &&
                        response.data.errors[0]
                    ) {
                        errMessage = response.data.errors[0].detail;
                    }
                    this.retryLogin = retry;
                    setLogInFailure(feed, errMessage);
                });
        }
        return null;
    }

    render() {
        const {
            isLoggedIn,
            auth,
            loginType,
            isLoading,
            isError,
            isIncorrectCredentials,
            feed,
            registrationLink,
            showGamblingCompliance,
            actionBarClass,
            initialValues,
            cookieName,
        } = this.props;
        const shouldRedirect = isLoggedIn || this.state.shouldNavigateBack;
        const isGetAuthFailed = (auth && auth.isError) || false;
        const authUrl = (auth && auth.authUrl) || '';

        return (<Login
            loginType={loginType}
            isLoading={isLoading}
            shouldRedirect={shouldRedirect}
            isLoginFailed={isError}
            isGetAuthFailed={isGetAuthFailed}
            isIncorrectCredentials={isIncorrectCredentials}
            authUrl={authUrl}
            feed={feed}
            registrationLink={registrationLink}
            showGamblingCompliance={showGamblingCompliance}
            actionBarClass={actionBarClass}
            initialValues={initialValues}
            onRetry={this.retry}
            onLoginSubmit={this.onLoginSubmit}
            onLoginSuccess={this.onLoginSuccess}
            onNavigateBack={this.onNavigateBack}
            onErrorCancel={this.cancelError}
            onSaveUsernameChange={this.onSaveUsernameChange}
            cookieName={cookieName}
        />);
    }
}

LoginContainer.defaultProps = {
    isLoading: false,
    isError: false,
    actionBarClass: '',
    showGamblingCompliance: false,
    isLoggedIn: false,
    registrationLink: '',
    auth: null,
    isIncorrectCredentials: false,
    formValues: null,
    cookieName: '',
}

LoginContainer.propTypes = {
    initialValues: PropTypes.shape({
        saveUsername: PropTypes.bool,
        username: PropTypes.string,
    }).isRequired,
    feed: PropTypes.string.isRequired,
    apiName: PropTypes.string.isRequired,
    loginType: PropTypes.string.isRequired,

    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    actionBarClass: PropTypes.string,
    showGamblingCompliance: PropTypes.bool,
    auth: PropTypes.shape({
        authUrl: PropTypes.string,
        type: PropTypes.string,
        isError: PropTypes.bool,
    }),
    formValues: PropTypes.shape({
        username: PropTypes.string,
        password: PropTypes.string,
        saveUsername: PropTypes.bool,
    }),
    isLoggedIn: PropTypes.bool,
    registrationLink: PropTypes.string,
    isIncorrectCredentials: PropTypes.bool,
    cookieName: PropTypes.string,

    getUserInfo: PropTypes.func.isRequired,
    destroyLoginForm: PropTypes.func.isRequired,
    saveBookmakerUsername: PropTypes.func.isRequired,
    removeBookmakerUsername: PropTypes.func.isRequired,
    clearError: PropTypes.func.isRequired,
    onLogin: PropTypes.func.isRequired,
    onLoginSuccess: PropTypes.func.isRequired,
    setLogIn: PropTypes.func.isRequired,
    setLogInFailure: PropTypes.func.isRequired,
    setLogInSuccess: PropTypes.func.isRequired,
    loadBookmakerLoginInfo: PropTypes.func.isRequired,
    clearFreeBets: PropTypes.func.isRequired,
    showGdprPopup: PropTypes.func.isRequired,
}

const bookmakerInfoSelector =
    bookmakersListSelectors.createBookmakerInfoSelector(
        routerSelectors.feedSelector,
    );

const formValuesSelector = getFormValues('loginForm');

const loginPageSelector = state => state.loginPage;

const loginSelector = createSelector([
    loginPageSelector,
    bookmakerInfoSelector,
    formValuesSelector,
    bookmakersInfoSelectors.usernameSelector,
],
    (
        loginPage,
        bookmakerInfo,
        formValues,
        storedUsername,
    ) => {
        const initialValues = storedUsername
                ? { saveUsername: true, username: storedUsername }
                : { saveUsername: false, username: '' };
        return {
            ...loginPage,
            ...bookmakerInfo,
            initialValues,
            formValues,
        };
    },
);

const mapDispatchToProps = {
    getUserInfo: bookmakersListActions.getUserInfo,
    setLogIn: bookmakersListActions.setLogIn,
    setLogInFailure: bookmakersListActions.setLogInFailure,
    setLogInSuccess: bookmakersListActions.setLogInSuccess,
    onLogin: actions.requestLogin,
    onLoginSuccess: actions.loginSuccess,
    loadBookmakerLoginInfo: actions.loadBookmakerLoginInfo,
    saveBookmakerUsername: bookmakersInfoActions.saveUsername,
    removeBookmakerUsername: bookmakersInfoActions.removeUsername,
    clearError: actions.loginClearError,
    destroyLoginForm: destroy.bind(null, 'loginForm'),
    hideErrorModal: actions.loginHideErrorModal,
    clearFreeBets: freeBetsActions.clearFreeBets,
    showGdprPopup: gdprActions.showGdprPopup,
};

export default connect(
    loginSelector,
    mapDispatchToProps,
)(LoginContainer);



// WEBPACK FOOTER //
// /src/js/common/loginPage/Login.container.jsx